import { localize } from "@gtmhub/localization";
import TreeNode from "@gtmhub/shared/tree-renderer/tree-node";

export type OrganizationTreeNodeOptions = {
  isMemberListCollapsed?: boolean;
  hasManagePeoplePermissions?: boolean;
  maxTooltipLength?: number;
  showSomeMembers?: boolean;
  someMembersLimit?: number;
};

export default class OrganizationTreeNode extends TreeNode {
  //// Layout cache ////
  // Dimensions cache used by this.calculateClientSize(). We need the values cached, because the browser needs
  // way too much time when asked to provide ".clientHeight" and ".clientWidth".
  private static dimensions = {
    nodePadding: null,
    singleRowHeight: null,
    memberRowHeight: null,
    memberRowPaddingTop: null,
    memberRowPaddingBottom: null,
    showMoreHeight: null,
    width: null,
  };

  private static selectors = {
    root: ".tree-container",
  };

  //// Internal Options ////
  private isShowMoreVisible = false;

  //// Options ////
  private isMemberListCollapsed = false;
  private hasManagePeoplePermissions = false;
  private maxTooltipLength = 56; /// Max char length for team/manager/member name
  private showSomeMembers = false;
  private someMembersLimit = 5;

  //// Event Handlers ////
  // Event handlers here are here just for easier debugging. In order to make real use of them,
  // reassign these function to a real handler in some controller.
  public static handleTeamNameClick(id: string) {
    // eslint-disable-next-line
    console.info(`handleTeamNameClick: ${id}`);
  }

  public static handleToggleChildren(id: string) {
    // eslint-disable-next-line
    console.info(`handleToggleChildren: ${id}`);
  }

  public static handleManagerClick(id: string, teamId: string) {
    // eslint-disable-next-line
    console.info(`handleManagerClick: ${id || "no manager"}, teamId: ${teamId}`);
  }

  public static handleTeamMemberClick(id: string) {
    // eslint-disable-next-line
    console.info(`handleTeamMemberClick: ${id}`);
  }

  public static handleContextMenuClick(event: Event, rowSelector: string, menuSelector: string, teamId: string, userId: string) {
    // eslint-disable-next-line
    console.info(`handleContextMenuClick: rowSelector: "${rowSelector}", menuSelector: "${menuSelector}", teamId: "${teamId}", userId: "${userId}"`);
  }

  public static handleTeamDrag(event: Event, teamId: string, teamColor: string) {
    // eslint-disable-next-line
    console.info(`handleTeamDrag: ${teamId}, color: ${teamColor}`);
  }

  public static handleTeamDrop(teamId: string) {
    // eslint-disable-next-line
    console.info(`handleTeamDrop: ${teamId}`);
  }

  public static handleHideTeamMembersClick(teamId: string) {
    // eslint-disable-next-line
    console.info(`handleHideTeamMembersClick: ${teamId}`);
  }

  public static handleShowTeamMembersClick(teamId: string) {
    // eslint-disable-next-line
    console.info(`handleShowTeamMembersClick: ${teamId}`);
  }

  /**
   * getCssVar
   * If one the given cache property in this.dimensions is NULL, this function looks for "varName" css variable and assigns
   * the value. If the cache property is set, nothing will happen.
   *
   * The value is assumed to be amount of pixels and is parsed as a such.
   *
   * @param {CSSStyleDeclaration} styles
   * @param {string} varName
   * @param {string} dimensionPropertyName
   * @return {void}
   */
  private getCssVar(styles: CSSStyleDeclaration, varName: string, dimensionPropertyName: string) {
    if (OrganizationTreeNode.dimensions[dimensionPropertyName] === null) {
      const value = Number.parseInt(styles.getPropertyValue(varName));
      OrganizationTreeNode.dimensions[dimensionPropertyName] = Number.isNaN(value) ? OrganizationTreeNode.dimensions[dimensionPropertyName] : value;
    }
  }

  /**
   * getDimensionsIfMissing()
   * Populates the dimension cache or does nothing if already set.
   *
   * @return {void}
   */
  private getDimensionsIfMissing() {
    const tree = document.querySelector(OrganizationTreeNode.selectors.root);
    if (!tree) {
      return;
    }

    const styles = getComputedStyle(tree);

    this.getCssVar(styles, "--tree-node-width", "width");
    this.getCssVar(styles, "--tree-node-padding-vertical", "nodePadding");
    this.getCssVar(styles, "--one-element-row-height", "singleRowHeight");
    this.getCssVar(styles, "--list-element-row-height", "memberRowHeight");
    this.getCssVar(styles, "--list-padding-top", "memberRowPaddingTop");
    this.getCssVar(styles, "--list-padding-bottom", "memberRowPaddingBottom");
    this.getCssVar(styles, "--members-info-row-height", "showMoreHeight");
  }

  /**
   * countMembers
   * Returns the number of all team members visible on screen or not.
   *
   * @return {number}
   */
  private countMembers(): number {
    return this.dataNode.data && Array.isArray(this.dataNode.data["membersInfo"]) ? this.dataNode.data["membersInfo"].length : 0;
  }

  /**
   * countTeamMembers
   * self-explanatory.
   *
   * @return {number}
   */
  private countVisibleMembers(): number {
    if (this.isMemberListCollapsed && !this.showSomeMembers) {
      return 0;
    } else if (this.isMemberListCollapsed && this.showSomeMembers) {
      return Math.min(this.countMembers(), this.someMembersLimit);
    } else {
      return this.countMembers();
    }
  }

  /**
   * calculateHeight
   * Calcualtes the height of the HTML representation of the node.
   *
   * @return {number}
   */
  private calculateHeight(): number {
    if (this.isHidden) {
      return 0;
    }

    return (
      2 * OrganizationTreeNode.dimensions.nodePadding +
      2 * OrganizationTreeNode.dimensions.singleRowHeight + // 2 * singleRowHeight = team name + manager name
      OrganizationTreeNode.dimensions.memberRowPaddingTop +
      this.countVisibleMembers() * OrganizationTreeNode.dimensions.memberRowHeight +
      OrganizationTreeNode.dimensions.memberRowPaddingBottom +
      (this.isShowMoreVisible ? OrganizationTreeNode.dimensions.showMoreHeight : 0)
    );
  }

  /**
   * calculateClientSize
   * The base function uses .clientHeight and .clientWidth properties. However,
   * accessing them is quite slow with > 1000 nodes (it may take 10 ms or more per node).
   * For this reason, here we calculate the dimensions of all building blocks and cache them.
   * Then we do simple adding to sum up the height and the width of the node, as opposed to DOM operations.
   *
   * @return { height: number, width: number }
   */
  protected calculateClientSize(): { height: number; width: number } {
    this.getDimensionsIfMissing();

    return {
      height: this.calculateHeight(),
      width: !this.isHidden && OrganizationTreeNode.dimensions.width ? OrganizationTreeNode.dimensions.width : 0,
    };
  }

  /**
   * getTeamColor
   * Returns team color data if available or the default color otherwise.
   *
   * @return {string} CSS color
   */
  private getTeamColor(): string {
    return this.dataNode.data && this.dataNode.data["color"] ? this.dataNode.data["color"] : "#fff";
  }

  /**
   * getTeamName
   * Returns team name data
   *
   * @return {string}
   */
  private getTeamName(): string {
    return this.dataNode.data ? this.dataNode.data["name"] : "";
  }

  /**
   * createSection
   * Generates a standard section organization node section with customizable styles and inner HTML.
   *
   * @param {string} wrapperClassName
   * @param {string} linkClassName
   * @param {string} innerHTML
   * @param {Function} clickHandler
   * @return {HTMLDivElement}
   */
  private createSection(wrapperClassName: string, linkClassName: string, innerHTML: string, clickHandler): HTMLDivElement {
    const a = document.createElement("a");
    a.className = linkClassName;
    a.innerHTML = innerHTML;
    a.addEventListener("click", clickHandler);

    const wrapper = document.createElement("div");
    wrapper.className = wrapperClassName;
    wrapper.appendChild(a);
    return wrapper;
  }

  /**
   * createThreeDotButton
   * The tree dots that open the context menu. The menu itself is not included, but rather the handler function is expected to
   * generate and position the actual Angular menu.
   *
   * @param {Function} clickHandler
   * @return {HTMLElement}
   */
  private createThreeDotButton(clickHandler): HTMLElement {
    const dropDownActionsMenu = document.createElement("dropdown-actions-menu");
    const uiActionsDD = document.createElement("div");
    const threeDotButton = document.createElement("div");

    dropDownActionsMenu.className = "ml-auto";
    dropDownActionsMenu.setAttribute("data-test-id", "org-chart-ellipsis-menu");
    uiActionsDD.className = "gh-ui-actions-dd dropdown";
    threeDotButton.className = "toggle large gh-font-icon-menu";
    threeDotButton.addEventListener("click", clickHandler);

    uiActionsDD.appendChild(threeDotButton);
    dropDownActionsMenu.appendChild(uiActionsDD);

    return dropDownActionsMenu;
  }

  /**
   * createShowMembersButton
   * Creates both the "show" members button with the appropriate labels based on the display mode.
   *
   * @param {void}
   * @return {HTMLElement}
   */
  private createShowMembersButton(): HTMLElement {
    const someMembersDifference = this.countMembers() - this.someMembersLimit;

    // show members
    const showMembersButton = document.createElement("a");
    showMembersButton.textContent = localize("show_members");
    showMembersButton.addEventListener("click", () => OrganizationTreeNode.handleShowTeamMembersClick(this.dataNode.id));
    showMembersButton.className = "ml-29px info-label";
    showMembersButton.setAttribute("data-test-id", "show-members-btn-org-chart");

    if (this.showSomeMembers) {
      showMembersButton.classList.add("show-some-team-members");

      if (someMembersDifference === 1) {
        showMembersButton.textContent = localize("show_1_more_member");
      } else if (someMembersDifference > 1) {
        showMembersButton.textContent = localize("show_n_more_members", { n: someMembersDifference });
      }
    } else {
      showMembersButton.classList.add("show-team-members");
    }

    return showMembersButton;
  }

  /**
   * createHideMembersButton
   * Creates both the "hide" members button with the appropriate labels based on the display mode.
   *
   * @param {void}
   * @return {HTMLElement}
   */
  private createHideMembersButton(): HTMLElement {
    const someMembersDifference = this.countMembers() - this.someMembersLimit;

    // hide members
    const hideMembersButton = document.createElement("a");
    hideMembersButton.className = "ml-29px info-label hide-team-members";
    hideMembersButton.textContent = localize("hide_members");
    hideMembersButton.addEventListener("click", () => OrganizationTreeNode.handleHideTeamMembersClick(this.dataNode.id));

    if (this.showSomeMembers) {
      if (someMembersDifference === 1) {
        hideMembersButton.textContent = localize("hide_1_member");
      } else if (someMembersDifference > 1) {
        hideMembersButton.textContent = localize("hide_n_members", { n: someMembersDifference });
      }
    }

    return hideMembersButton;
  }

  /**
   * createChildrenToggleHandle
   * The collapse/expand children handle
   *
   * @param {void}
   * @return {HTMLDivElement}
   */
  private createChildrenToggleHandle(): HTMLDivElement {
    let innerHTML = "";
    if (this.dataNode.folded) {
      innerHTML = `<span class="children-count">${this.dataNode.children.length}</span>`;
    } else {
      innerHTML = `<span class="fold-children"></span>`;
    }

    return this.createSection("children-toggle-wrapper", "children-toggle", innerHTML, () => OrganizationTreeNode.handleToggleChildren(this.dataNode.id));
  }

  /**
   * create
   * The tooltip that appears when hovering a team or a person name.
   *
   * @param {string} content
   * @return {HTMLDivElement}
   */
  private createTooltip(content: string): HTMLDivElement {
    const tooltip = document.createElement("div");
    tooltip.className = "tooltip top fade in";
    tooltip.innerHTML = `
      <div class="tooltip-arrow"></div>
      <div class="tooltip-inner">${content}</div>
    `;
    return tooltip;
  }

  /**
   * createDragHandle
   * The colored drag handle for dragging a team and making it a child of another team.
   *
   * @return {HTMLElement}
   */
  private createDragHandle(): HTMLElement {
    const dragHandle = document.createElement("span");
    dragHandle.className = "drag-handle";
    dragHandle.style.backgroundColor = this.getTeamColor();
    dragHandle.addEventListener("mousedown", (event) => OrganizationTreeNode.handleTeamDrag(event, this.dataNode.id, this.getTeamColor()));

    return dragHandle;
  }

  /**
   * createDropTarget
   * The target where the dragged team is dropped, so that it would become a subteam of another team.
   *
   * @return {HTMLElement}
   */
  private createDropTarget(): HTMLElement {
    const dropTarget = document.createElement("span");
    dropTarget.className = `drop-handle drop-target-${this.dataNode.id}`;
    dropTarget.addEventListener("mouseup", () => OrganizationTreeNode.handleTeamDrop(this.dataNode.id));

    return dropTarget;
  }

  /**
   * createTeamNameSection
   * Team name header
   *
   * @param {void}
   * @return {HTMLDivElement}
   */
  private createTeamNameSection(): HTMLDivElement {
    const teamId = this.dataNode.id;
    const teamData = this.dataNode.data;
    const teamColor = teamData["color"] || "#87a6bc";
    const teamAvatar = teamData["avatar"] || "users_multiple-11";
    const containerClass = "team-name-container";
    const teamImageTemplate = teamData["picture"]
      ? `<img class="avatar-new circle s-20" src="${teamData["picture"]}" />`
      : `<div class="avatar-new circle icon-avatar s-20" style="background: ${teamColor}">
          <span class="nc-icon-outline icon ${teamAvatar}"></span>
        </div>
      `;

    const teamName = this.getTeamName();
    const innerHTML = `
      <div class="avatar mock-hover-title">
          ${teamImageTemplate}
      </div>
      <span class="team-name">${teamName}</span>
    `;
    const section = this.createSection(`${containerClass} section-devider`, "team-name-wrapper", innerHTML, () =>
      OrganizationTreeNode.handleTeamNameClick(this.dataNode.id)
    );

    const teamNameDataTestId = `team-name-org-chart-${teamName}`;
    section.setAttribute("data-test-id", teamNameDataTestId);

    if (teamName.length > this.maxTooltipLength) {
      section.appendChild(this.createTooltip(teamName));
    }

    if (this.hasManagePeoplePermissions) {
      section.appendChild(
        this.createThreeDotButton((event) => OrganizationTreeNode.handleContextMenuClick(event, `.${containerClass}`, "team-context-menu", teamId, ""))
      );
    }

    return section;
  }

  /**
   * createManagerSection
   * Manager section
   *
   * @param {void}
   * @return {HTMLDivElement}
   */
  private createManagerSection(): HTMLDivElement {
    const managerId = this.dataNode.data["managerInfo"] && this.dataNode.data["managerInfo"]["id"] ? this.dataNode.data["managerInfo"]["id"] : null;
    let managerName = "";
    let clickHandler = () => OrganizationTreeNode.handleManagerClick(managerId, this.dataNode.id);
    let linkClassName = "";
    let innerHTML = "";
    const containerClass = "manager-container";

    // name + avatar
    if (managerId) {
      managerName = `${this.dataNode.data["managerInfo"].firstName} ${this.dataNode.data["managerInfo"].lastName}`;
      linkClassName = "manager-name-wrapper";
      innerHTML = `
        <img class="avatar mock-hover-title" src="${this.dataNode.data["managerInfo"].picture}" onerror="this.src = '/dist/img/avatars/unknown.svg'">
        <div class="manager-info">
          <span class="manager-name">${managerName}</span>
          <span class="info-label">${localize("team_manager")}</span>
        </div>`;
    } else {
      linkClassName = "no-manager-label";
      innerHTML = `
        <img class="avatar" src="/dist/img/avatars/unknown.svg">
        ${localize("no_manager_assigned")}`;

      if (!this.hasManagePeoplePermissions) {
        clickHandler = () => null;
      }
    }

    let sectionClassNames = containerClass;
    sectionClassNames += this.countMembers() > 0 ? " section-devider" : "";
    const section = this.createSection(sectionClassNames, linkClassName, innerHTML, clickHandler);

    // tooltip
    if (managerId && managerName.length > this.maxTooltipLength) {
      section.appendChild(this.createTooltip(managerName));
    }

    // three-dot menu
    if (this.hasManagePeoplePermissions && managerId !== null) {
      section.appendChild(
        this.createThreeDotButton((event) =>
          OrganizationTreeNode.handleContextMenuClick(event, `.${containerClass}`, "manager-context-menu", this.dataNode.id, managerId)
        )
      );
    }

    // test id
    if (managerName) {
      section.setAttribute("data-test-id", `team-manager-org-chart-${managerName}`);
    }

    const noManagerNameItem = section.querySelector(".no-manager-label");
    if (noManagerNameItem) {
      noManagerNameItem.setAttribute("data-test-id", "no-team-manager-label-org-chart");
    }

    return section;
  }

  /**
   * createMembersSection
   * Team members list
   *
   * @param {void}
   * @return {HTMLDivElement}
   */
  private createMembersSection(): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "team-members-container";
    if (this.isMemberListCollapsed) {
      section.classList.add("none");
    }

    this.dataNode.data["membersInfo"].forEach((m) => {
      const memberName = `${m.firstName} ${m.lastName}`;
      const avatar = document.createElement("img");
      avatar.className = "avatar mock-hover-title";
      avatar.onerror = function () {
        this.src = "/dist/img/avatars/unknown.svg";
      };
      avatar.src = m.picture;

      const link = document.createElement("a");
      link.className = "member-name-wrapper";
      link.addEventListener("click", () => OrganizationTreeNode.handleTeamMemberClick(m.id));
      link.innerHTML = `<span class="member-name">${memberName}</span>`;

      const row = document.createElement("div");
      row.className = "team-member";

      row.appendChild(avatar);
      row.appendChild(link);

      if (memberName.length > this.maxTooltipLength) {
        row.appendChild(this.createTooltip(memberName));
      }

      if (!m.isActive) {
        const inactiveLabel = document.createElement("span");
        inactiveLabel.className = "info-label";
        inactiveLabel.textContent = localize("deactivated");
        row.appendChild(inactiveLabel);
      }

      if (this.hasManagePeoplePermissions) {
        const clickHandler = (event) => OrganizationTreeNode.handleContextMenuClick(event, `.${row.className}`, "team-member-context-menu", this.dataNode.id, m.id);
        row.appendChild(this.createThreeDotButton(clickHandler));
      }

      row.setAttribute("data-test-id", `team-member-org-chart-${memberName}`);

      section.appendChild(row);
    });

    this.isShowMoreVisible = this.countMembers() > 0 && (!this.showSomeMembers || this.countMembers() > this.someMembersLimit);
    if (this.isShowMoreVisible) {
      section.appendChild(this.createShowMembersButton());
      section.appendChild(this.createHideMembersButton());
    }

    return section;
  }

  /**
   * create
   * Generates the raw HTML of the node. We do it instead of Angular, because it is more than 10 times faster this way.
   *
   * @return {HTMLDivElement}  The tree node
   */
  protected create(): HTMLDivElement {
    const nodeElement = document.createElement("div");

    nodeElement.setAttribute("data-test-id", `node-team-in-org-chart-${this.getTeamName()}`);
    nodeElement.style.borderLeftColor = this.getTeamColor();

    if (this.dataNode.children) {
      nodeElement.appendChild(this.createChildrenToggleHandle());
    }
    nodeElement.appendChild(this.createTeamNameSection());
    nodeElement.appendChild(this.createManagerSection());

    if (this.countMembers() > 0) {
      nodeElement.appendChild(this.createMembersSection());
    }

    nodeElement.appendChild(this.createDragHandle());
    nodeElement.appendChild(this.createDropTarget());

    this.addStandardClasses(nodeElement);

    return nodeElement;
  }

  public setOptions(options: OrganizationTreeNodeOptions): this {
    this.isMemberListCollapsed = options.isMemberListCollapsed !== undefined ? options.isMemberListCollapsed : this.isMemberListCollapsed;
    this.hasManagePeoplePermissions = options.hasManagePeoplePermissions !== undefined ? options.hasManagePeoplePermissions : this.hasManagePeoplePermissions;
    this.maxTooltipLength = options.maxTooltipLength !== undefined ? options.maxTooltipLength : this.maxTooltipLength;
    this.showSomeMembers = options.showSomeMembers !== undefined ? options.showSomeMembers : this.showSomeMembers;
    this.someMembersLimit = options.someMembersLimit !== undefined ? options.someMembersLimit : this.someMembersLimit;

    return this;
  }

  /**
   * collapseMembers
   * Collapses the member list based on the current display mode, leaving a maximum of 0 or 5 members.
   * It's a simple CSS operation, so it comes into effect immediatelly. However, tree re-render is needed,
   * to readjust the spacing between the current node and its' neighbourghs.
   */
  public collapseMembers(): void {
    this.isMemberListCollapsed = true;

    const membersSection = document.querySelector(`#${this.htmlId} .team-members-container`);
    if (membersSection) {
      membersSection.classList.add("none");
    }
  }

  /**
   * collapseMembers
   * Expands the member list based on the current display mode, showing either the entire list or 5 members.
   * It's a simple CSS operation, so it comes into effect immediatelly. However, tree re-render is needed,
   * to readjust the spacing between the current node and its' neighbourghs.
   */
  public expandMembers(): void {
    this.isMemberListCollapsed = false;

    const membersSection = document.querySelector(`#${this.htmlId} .team-members-container`);
    if (membersSection) {
      membersSection.classList.remove("none");
    }
  }
}
